<template>
  <div class="contact-slide-in trap" :id="id">
    <button
      class="btn btn-cta"
      v-show="false"
      @click="toggleSliding"
      v-text="texts.bookMeeting"
    />

    <transition name="slide-in-contact-form">
      <div
        class="lavendla-slide-in-container contact-slide-in"
        data-cy="sliding-contact-form"
        v-if="isActive"
      >
        <div class="overlay" @click="slideOut" />
        <div class="lavendla-slide-in-wrapper">
          <transition-group
            class="lavendla-slide-in__actions"
            name="lavendla-slide-in__actions"
            tag="div"
          >
            <button :key="1" @click.prevent="slideOut">
              <icon-close />
            </button>
            <h2
              class="lavendla-slide-in__title"
              :key="2"
              v-text="formHeading"
            />
            <button
              v-if="showBackArrow && formType"
              :key="3"
              @click.prevent="updateFormType()"
            >
              <svg class="icon"><use href="#arrow-left" /></svg>
            </button>
          </transition-group>

          <div class="lavendla-slide-in">
            <div class="lavendla-slide-in__body">
              <transition mode="out-in" name="fade-to-left">
                <div
                  class="city-jurist slide-in-form-wrapper"
                  v-if="formType === 'city' || formType === 'jurist'"
                  :key="'city-jurist'"
                >
                  <div
                    class="lavendla-slide-in__desc"
                    @click="
                      gtmPhoneCallTrigger(
                        $event,
                        `Contact Form Modal - ${formType}`
                      )
                    "
                    v-html="formDesc"
                  />
                  <ContactForm
                    class="light-form"
                    v-model:date-time-open="dateTimeOpen"
                    v-model:form-sent="formSent"
                    :page-title="pageTitle"
                    :post-type="formType"
                    :therapy-page-competence="therapyPageCompetence"
                  />
                </div>

                <div
                  class="city-jurist slide-in-form-wrapper"
                  v-else-if="formType === 'therapy'"
                  :key="'therapy-choices'"
                >
                  <div class="lavendla-slide-in__desc" v-html="formDesc" />
                  <therapy-choices />
                </div>

                <FormTypeSelector
                  v-else
                  :desc-text="formSelectorDesc"
                  :locale="locale"
                  :text-content="textContent"
                  :texts="texts"
                  @update-form-type="updateFormType"
                />
              </transition>

              <div class="cta-wrapper" v-if="formType !== 'therapy'">
                <h3
                  v-if="textContent.cta_title"
                  v-html="textContent.cta_title"
                />
                <p v-if="textContent.cta_text" v-html="textContent.cta_text" />
                <Cards v-if="cards" :cards="cards" :request-type="formType" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { usePhoneNumber } from 'Main/composables/usePhoneNumber';
import ContactForm from 'Main/contact-form/components/ContactForm.vue';
import IconClose from 'Main/icons-vue/IconClose.vue';
import debounce from 'lodash-es/debounce';
import Cards from './Cards.vue';
import FormTypeSelector from './FormTypeSelector.vue';
import TherapyChoices from './TherapyChoices.vue';

export default {

  beforeUnmount() {
    window.removeEventListener('resize', debounce(this.setWindowWidth, 50));
    document.removeEventListener('keyup', (event) => this.escKeyClose(event));
    window.removeEventListener(
      'city-storage-updated-event',
      this.maybeUpdatePhoneNumber,
    );
  },

  components: {
    Cards,
    ContactForm,
    FormTypeSelector,
    IconClose,
    TherapyChoices,
  },

  computed: {
    formDesc() {
      switch (this.formType) {
        case 'city':
          return this.handlePhoneNumberShortcodeInDesc(
            this.texts.contactFormDescFuneral
          );
        case 'jurist':
          return this.handlePhoneNumberShortcodeInDesc(
            this.texts.contactFormDescLaw
          );
        case 'therapy':
          return this.handlePhoneNumberShortcodeInDesc(
            this.texts.contactFormDescTherapy
          );
        default:
          return this.handlePhoneNumberShortcodeInDesc(
            this.texts.contactFormDescFuneral
          );
      }
    },

    formHeading() {
      switch (this.formType) {
        case 'city':
          return this.texts.contactFormHeadingFuneral;
        case 'jurist':
          return this.texts.contactFormHeadingLaw;
        case 'therapy':
          return this.texts.contactFormHeadingTherapy;
        default:
          return this.textContent.title;
      }
    },

    formSelectorDesc() {
      return this.handlePhoneNumberShortcodeInDesc(this.textContent.text);
    },

    isMobile() {
      return this.windowWidth < 992;
    },
  },

  data() {
    const content = JSON.parse(this.$mountEl.dataset.content);
    const cards = {
      city: content ? content.cta_cards_funeral : {},
      default: content ? content.cta_cards : {},
      jurist: content ? content.cta_cards_law : {},
      therapy: content ? content.cta_cards_therapy : {},
    };

    return {
      cards,
      content: content || {},
      currentSite: theme_vars.currentSite,
      dateTimeOpen: false,
      formSent: false,
      formType: '',
      gutenbergType: this.$mountEl.dataset.gutenbergType,
      id: this.$mountEl.id,
      isActive: false,
      locale: theme_vars.locale,
      pageTitle: this.$mountEl.dataset.pageTitle,
      pageType: this.$mountEl.dataset.pageType,
      postType: this.$mountEl.dataset.postType,
      showBackArrow: true,
      textContent: content ? content.text_content : {},
      texts: theme_vars.contactTexts,
      therapyPageCompetence: '',
    };
  },

  methods: {

    /**
     * Slide out with Escape key.
     *
     * @param {Event} event
     */
    escKeyClose(event) {
      if (event.defaultPrevented) {
        return;
      }

      const key = event.key || event.keyCode;

      if (key === 'Escape' || key === 'Esc' || key === 27) {
        this.slideOut();
      }
    },

    handlePhoneNumberShortcodeInDesc(text) {
      if (!text) {
        return '';
      }

      const template = document.createElement('template');
      const descHtml = text.trim();
      template.innerHTML = descHtml;

      const phoneNumberElement = template.content.firstChild.querySelector(
        '.lavendla-phone-number'
      );

      if (!phoneNumberElement) {
        return text;
      }

      // Build the new element
      const newNode = document.createElement('a');
      const newContent = document.createTextNode(this.phoneNumber);
      newNode.appendChild(newContent);
      newNode.href = this.phoneNumberTel;

      // Replace the old element with the new one
      template.content.firstChild.replaceChild(newNode, phoneNumberElement);

      // Handle dynamic phone numbers
      this.ctmRunNow(template.content.firstChild);

      return template.innerHTML;
    },

    lockBody(lock = true) {
      const body = document.querySelector('body');
      if (!this.isMobile) {
        if (lock) {
          body.style.overflow = 'hidden';
        } else {
          setTimeout(() => {
            body.style.overflow = 'visible';
          }, 400);
        }
      }
    },
    setWindowWidth() {
      this.windowWidth = window.innerWidth;
    },

    slideOut() {
      this.lockBody(false);
      this.isActive = false;
      this.formSent = false;
      window.contactFormContent = null;
    },

    /**
     * slideIn / slideOut.
     */
    toggleSliding() {
      if (this.formSent && this.isActive) {
        this.slideOut();
        return;
      }

      this.lockBody(!this.isActive);
      this.isActive = !this.isActive;
    },

    updateFormType(val = '') {
      this.formType = val;
    },
  },

  mounted() {
    window.addEventListener('resize', debounce(this.setWindowWidth, 50));
    document.addEventListener('keyup', (event) => this.escKeyClose(event));
    window.addEventListener('resize', () => {
      this.windowWidth = window.innerWidth;
    });

    window.addEventListener(
      'city-storage-updated-event',
      this.maybeUpdatePhoneNumber,
    );
    this.maybeUpdatePhoneNumber();

    // show cf automatically in city pages, places
    switch (this.pageType) {
      case 'city':
        this.formType = 'city';
        this.showBackArrow = false;
        break;
      case 'place':
        this.formType = 'city';
        this.showBackArrow = false;
        break;
      case 'jurist':
        this.formType = 'jurist';
        this.showBackArrow = false;
        break;
      case 'therapy':
        this.formType = 'therapy';
        this.therapyPageCompetence = this.postType;
        this.showBackArrow = false;
        break;
    }

    switch (this.gutenbergType) {
      case 'funeral':
        this.formType = 'city';
        this.showBackArrow = false;
        break;
      case 'law':
        this.formType = 'jurist';
        this.showBackArrow = false;
        break;
      case 'therapy':
        this.formType = 'therapy';
        this.showBackArrow = false;
        break;
    }
  },
  name: 'SlideInContactForm',

  setup() {
    const {
      phoneNumber,
      phoneNumberTel,
      hidePhoneNumber,
      isTherapyPage,
      countryPhoneCode,
      maybeUpdatePhoneNumber,
      updatePhoneNumber,
      ctmRunNow,
      ctmInit,
      gtmPhoneCallTrigger,
    } = usePhoneNumber();
    return {
      countryPhoneCode,
      ctmInit,
      ctmRunNow,
      gtmPhoneCallTrigger,
      hidePhoneNumber,
      isTherapyPage,
      maybeUpdatePhoneNumber,
      phoneNumber,
      phoneNumberTel,
      updatePhoneNumber,
    };
  },
};
</script>
